import React, { useRef, useEffect, useState } from "react";
import {
  Box,
  Container,
  Text,
  Heading,
  HStack,
  IconButton,
  Button,
  useColorMode,
  Flex,
  useBreakpointValue,
  Tooltip,
  Hide,
  Show,
  SlideFade,
  ScaleFade,
} from "@chakra-ui/react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  AddIcon,
  MinusIcon,
  RepeatIcon,
  ViewOffIcon,
  MoonIcon,
  SunIcon,
} from "@chakra-ui/icons";
import { TFunction } from "i18next";
import { ContentItem } from "../libs/types";
import ChapterNavigation from "./ChapterNavigation";
import ChapterMenu from "./ChapterMenu";
import TableOfContent from "./TableOfContent";
import FallbackLanguageBar from "./FallbackLanguageBar";
import BookContent from "./BookContent";
import { smoothScrollTo } from "../utils/scrollUtils";
import useChapterNavigation from "../hooks/useChapterNavigation";

interface ReadModeProps {
  currentChapterIndex: number;
  totalContentItems: number;
  currentContentItem: ContentItem;
  contentItems: ContentItem[];
  handlePreviousChapter: () => void;
  handleNextChapter: () => void;
  exitFullPageMode: () => void;
  increaseFontSize: () => void;
  decreaseFontSize: () => void;
  resetFontSize: () => void;
  fontSize: number;
  roundedFontSize: number;
  isDefaultSize: boolean;
  actualFontSize: {
    base: string;
    lg: string;
  };
  headingFontSize: {
    base: string;
  };
  t: TFunction;
  i18n: any;
  prefersReducedMotion: boolean;
  isMobile: boolean | undefined;
  language: string;
  onOpenTableOfContents: () => void;
  fallbackLanguage: string | null;
  getLanguageDisplayName: (code: string, displayLanguage: string) => string;
}

const ReadMode: React.FC<ReadModeProps> = ({
  currentChapterIndex,
  totalContentItems,
  currentContentItem,
  contentItems,
  handlePreviousChapter,
  handleNextChapter,
  exitFullPageMode,
  increaseFontSize,
  decreaseFontSize,
  resetFontSize,
  fontSize,
  roundedFontSize,
  isDefaultSize,
  actualFontSize,
  headingFontSize,
  t,
  i18n,
  prefersReducedMotion,
  isMobile,
  language,
  onOpenTableOfContents,
  fallbackLanguage,
  getLanguageDisplayName,
}) => {
  const { colorMode, toggleColorMode } = useColorMode();
  const contentRef = useRef<HTMLDivElement>(null);
  const isMobileView = useBreakpointValue({ base: true, md: false });
  const containerRef = useRef<HTMLDivElement>(null);
  const [isTOCOpen, setIsTOCOpen] = useState(false);

  // Use the shared navigation hook
  const {
    isNavigating,
    handlePreviousChapter: enhancedPrevious,
    handleNextChapter: enhancedNext,
  } = useChapterNavigation({
    prefersReducedMotion,
    onPreviousChapter: handlePreviousChapter,
    onNextChapter: handleNextChapter,
    containerRef,
    contentRef,
    headerOffset: 80, // Offset for ReadMode to account for top navigation
  });

  return (
    <Box
      id="read-mode-container"
      ref={containerRef}
      height={{ base: "100%", md: "100vh" }}
      position="fixed"
      top="0"
      left="0"
      right="0"
      bottom="0"
      zIndex="modal"
      bg={colorMode === "light" ? "white" : "#1A202C"}
      overflowY="auto"
      pt={0}
      pb={{ base: "80px", md: 0 }}
      px={{ base: 0, md: 0 }}
      sx={{
        animation: "0.4s ease-out 0s 1 fadeInScale",
        "@keyframes fadeInScale": {
          "0%": {
            opacity: 0,
            transform: "scale(0.95)",
          },
          "100%": {
            opacity: 1,
            transform: "scale(1)",
          },
        },
        scrollBehavior: prefersReducedMotion ? "auto" : "smooth",
        WebkitOverflowScrolling: "touch",
      }}
    >
      <TableOfContent
        isOpen={isTOCOpen}
        onClose={() => setIsTOCOpen(false)}
        contentItems={contentItems}
        currentChapterIndex={currentChapterIndex}
        onChapterSelect={(index) => {
          if (index !== currentChapterIndex) {
            setIsTOCOpen(false);
            if (index < currentChapterIndex) {
              // Navigate backwards multiple chapters if needed
              for (let i = 0; i < currentChapterIndex - index; i++) {
                handlePreviousChapter();
              }
            } else {
              // Navigate forwards multiple chapters if needed
              for (let i = 0; i < index - currentChapterIndex; i++) {
                handleNextChapter();
              }
            }
          }
        }}
        isMobile={isMobile}
        t={t}
      />

      <Container
        maxW={{ base: "container.md", lg: "container.lg", xl: "container.xl" }}
        px={{ base: 2, md: 2 }}
        pt={2}
      >
        {/* Isolated menu container with specific background */}
        <Box
          width="100%"
          background="none"
          mb={4}
          sx={{
            maxWidth: { base: "100%", md: "100%", lg: "95%", xl: "90%" },
            mx: "auto",
            "& > div": {
              background: "none !important", // Force remove any background from direct children
            },
          }}
        >
          <ChapterMenu
            currentContentItem={currentContentItem}
            exitFullPageMode={exitFullPageMode}
            increaseFontSize={increaseFontSize}
            decreaseFontSize={decreaseFontSize}
            resetFontSize={resetFontSize}
            fontSize={fontSize}
            roundedFontSize={roundedFontSize}
            isDefaultSize={isDefaultSize}
            t={t}
            toggleColorMode={toggleColorMode}
            onOpenTableOfContents={() => setIsTOCOpen(true)}
          />
        </Box>

        {/* Content Display with integrated navigation */}
        <Box
          position="relative"
          sx={{
            touchAction: "auto", // Allow normal touch events
          }}
          id="readmode-content-wrapper"
        >
          {currentContentItem && (
            <BookContent
              ref={contentRef}
              key={`content-${currentChapterIndex}-${language}`}
              chapter={currentContentItem.chapter}
              text={currentContentItem.text}
              prefersReducedMotion={prefersReducedMotion}
              isMobile={isMobile}
              t={t}
              fontSize={actualFontSize}
              headingFontSize={headingFontSize}
              currentChapterIndex={currentChapterIndex}
              totalContentItems={totalContentItems}
              handlePreviousChapter={enhancedPrevious}
              handleNextChapter={enhancedNext}
              onOpenTableOfContents={() => setIsTOCOpen(true)}
              showNavigation={true}
              isNavigating={isNavigating}
              navigationVariant="default"
              language={language}
              showBottomMargin={false}
            />
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default ReadMode;
